import { Badge, Button, Dialog, Input } from "@/components/ui";
import * as React from "react";
import { PlayersServices } from "@/services/bedrock";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
interface Props {
  open: boolean;
  playerId: number;
  onOpenChange: (open: boolean) => void;
}

export function PlayerAliasesDialog({ open, playerId, onOpenChange }: Props) {
  const [saving, setSaving] = useState<boolean>(false);
  const [initialAliases, setInitialAliases] = useState<string[]>([]);
  const [aliases, setAliases] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!open) {
      setDraft("");
      return;
    }
    let cancelled = false;
    void (async () => {
      const player = await PlayersServices.getPlayer(playerId, ["aliases"]);
      if (!cancelled) {
        const aliases = player.aliases || [];
        setAliases(aliases);
        setInitialAliases(aliases);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId, open]);

  const hasChanges = useMemo(() => {
    return [...aliases].sort().join(",") !== [...initialAliases].sort().join(",");
  }, [aliases, initialAliases]);

  const saveAliases = async () => {
    setSaving(true);
    PlayersServices.updatePlayer(playerId, { aliases })
      .then(() => {
        toast.success("Aliases saved!");
        setInitialAliases(aliases);
        onOpenChange(false);
      })
      .catch(() => {
        toast.error("Failed to save aliases");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const commitDraft = () => {
    setAliases(prev => {
      const aliases = [...prev, ...draft.split(/[ ,]/)];
      const trimmedAliases = aliases.map(a => a.trim()).filter(a => a !== "");
      return Object.values(Object.fromEntries(trimmedAliases.map(a => [a.toLowerCase(), a])));
    });
    setDraft("");
  };

  const removeAlias = (index: number) => {
    setAliases(prev => prev.filter((_, i) => i !== index));
  };

  const onDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || (e.key === " " && draft.trim().length > 0)) {
      e.preventDefault();
      commitDraft();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="gap-0">
      <Dialog.Header>
        <Dialog.Title>Aliases</Dialog.Title>
        <Dialog.Description>
          Optional alternate identifiers for Player. Each identifier must be unique among all Players.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {aliases.map((alias, index) => (
              <Badge key={`${alias}-${index}`} variant="outline" className="max-w-full gap-0.5 pr-0.5 font-normal">
                <span className="max-w-[220px] truncate">{alias}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove alias ${alias}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => removeAlias(index)}
                >
                  <X className="size-3.5 shrink-0" />
                </Button>
              </Badge>
            ))}
          </div>

          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onDraftKeyDown}
            onBlur={commitDraft}
            placeholder="Type an alias, then Space or Enter"
          />
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={saveAliases} loading={saving} disabled={!hasChanges}>
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
